import {ConsumerInformation, EOrderStatus, Order, orderStore} from '../../../../../redux/entities/inventory/order';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import gridStyles from '../../../../GridStyles.module.scss';
import {bindActionCreators, Dispatch} from 'redux';
import {ConfirmationDialog} from '../../../../../components/util/ConfirmationDialog/ConfirmationDialog';
import IconButton from '../../../../../components/util/widgets/IconButton/IconButton';
import { Button, Paper } from '@mantine/core';
import {Navigate, useNavigate, useParams} from 'react-router-dom';
import {format, utcToZonedTime} from 'date-fns-tz';
import {parseISO} from 'date-fns';
import {CommonState} from '../../../../../redux';
import {localTz, localTzFriendlyFormat} from '../../../../../util/date';
import {formatCentsToUSD, propertyOf} from '../../../../../util';
import {formatIfPossibleUsPhone, isNullOrWhitespace} from '../../../../../util/string';
import OrderModal from './OrderModal';
import ProgramModal from './ProgramModal';
import HouseModal from './HouseModal';
import {AppTheme} from '../../../../../appTheme';
import {AxiosError} from 'axios/index';
import {getErrorResponseMessage} from '../../../../../util/http';
import Select from 'react-select';
import StyledMantineTable, { type MRT_ColumnDef } from '../../../../../components/util/StyledMantineTable/StyledMantineTable';
import useStandardPrint from '../../../../../hooks/useStandardPrint/useStandardPrint';
import { makeOrder } from '../../../../../redux/factory/inventory/order';
import { RoutePaths } from '../../../../../router/RoutePaths';
import AdministratorComponent from "../../../../../components/util/RoleGatedComponent/AdministratorComponent";
import AuditLogLoader from "../../../../private/AuditLog/AuditLogLoader";
import {programStore} from "../../../../../redux/entities/inventory/program";
import {houseStore} from "../../../../../redux/entities/inventory/house";
import {Text} from "@mantine/core";
import {RoleProtectedComponent} from "../../../../../components/util/RoleProtectedRoute/RoleProtectedComponent";

interface TFilterDD {value: 'all' | 'unapproved' | 'approved' | 'cancelled' | 'notCancelled'; label: string;}

const orderFilterDropdownOptions: TFilterDD[] = [
  {value: 'all', label: 'All Orders'},
  {value: 'unapproved', label: 'Unapproved Withdrawals'},
  {value: 'approved', label: 'Approved Withdrawals'},
  {value: 'cancelled', label: 'Cancelled Withdrawals'},
  {value: 'notCancelled', label: 'Do Not Show Cancelled Withdrawals'}
];

const defaultFilterValue = orderFilterDropdownOptions[2];

const ConnectedProgramName = connect((state: CommonState, ownProps: {programId: string; fallback: string}) => ({
  program: programStore.selectors.getById(state)(ownProps.programId)
}))(({program, fallback}: {program: any; fallback: string}) => <>{program?.name || fallback || '-'}</>);

const ConnectedHouseName = connect((state: CommonState, ownProps: {houseId: string; fallback: string}) => ({
  house: houseStore.selectors.getById(state)(ownProps.houseId)
}))(({house, fallback}: {house: any; fallback: string}) => <>{house?.name || fallback || '-'}</>);

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

function OrderGrid(props: Props) {
  const {orders, getOrderById, unapprovedOrders, approvedOrders, cancelledOrders, notCancelledOrders,
    actions: {updateOrderStatus, cancelOrder, deleteOrder}} = props;
  const [isDeleting, setIsDeleting] = useState('');
  const {id : paramsId} = useParams();
  const [editingOrderData, setEditingOrderData] = useState<{editing: boolean; id: string}>(
    {editing: !isNullOrWhitespace(paramsId), id: paramsId ?? ''});
  const [editingStatusOrder, setEditingStatusOrder] = useState<Order | null>(null);
  const [cancellingOrder, setCancellingOrder] = useState<Order | null>(null);
  const [deletingOrder, setDeletingOrder] = useState<Order | null>(null);
  const [creatingOrder, setCreatingOrder] = useState<Order | null>(null);
  const [viewingOrderId, setViewingOrderId] = useState<string>('');
  const [programModalOpened, setProgramModalOpened] = useState(false);
  const [houseModalOpened, setHouseModalOpened] = useState(false);
  // Local audit log drawer state
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEntityId, setAuditEntityId] = useState<string | undefined>(undefined);
  const [orderFilter, setOrderFilter] = useState<TFilterDD>(defaultFilterValue);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const printRef = useRef<HTMLDivElement | null>(null);
  const handleprint = useStandardPrint(printRef);
  const navigate = useNavigate();

  const getFieldName = propertyOf<Order>;
  const getConsumerInfoName = (value: keyof ConsumerInformation) => `${getFieldName('consumerInformation')}.${value}`;

  const filteredOrders = useMemo(() => {
    switch (orderFilter.value) {
      case 'unapproved':
        return unapprovedOrders;
      case 'approved':
        return approvedOrders;
      case 'cancelled':
        return cancelledOrders;
      case 'notCancelled':
        return notCancelledOrders;
      default: return orders;
    }
  }, [unapprovedOrders, approvedOrders, cancelledOrders, orders, orderFilter.value, notCancelledOrders]);

  const processStatusSubmit = async (order: Order) => {
    try {
      await updateOrderStatus(order.id, EOrderStatus.Approved);
    } catch (e: AxiosError | any) {
      setErrorMessage(getErrorResponseMessage(e));
    }
  };

  const renderDeletionModal = () => {
    if (!deletingOrder) return null;
    return (
      <ConfirmationDialog
        open={true}
        onAccept={async () => {
          await deleteOrder(deletingOrder.id);
          setDeletingOrder(null);
        }}
        onDecline={async () => {
          setDeletingOrder(null);
        }}
        prompt={`Are you sure you would like to permenantly delete ${deletingOrder.consumerInformation.name}'s
         order? This CANNOT be undone, it is recommended to leave orders cancelled.`}
        positiveText={`Delete order`}
        negativeText={'Cancel'}
        positiveVariant={'warning'}
        negativeVariant={'danger'}
      />
    );
  };

  const renderCancellationModal = () => {
    if (!cancellingOrder) return null;
    return (
      <ConfirmationDialog
        open={true}
        onAccept={async () => {
          await cancelOrder(cancellingOrder.id);
          setCancellingOrder(null);
        }}
        onDecline={async () => {
          setCancellingOrder(null);
        }}
        prompt={`Are you sure you would like to cancel ${cancellingOrder.consumerInformation.name}'s
         order? This can be undone.`}
        positiveText={`Mark as cancelled`}
        negativeText={'Cancel'}
        positiveVariant={'success'}
        negativeVariant={'danger'}
      />
    );
  };

  const renderStatusSubmitModal = () => {
    if (!editingStatusOrder) return null;
    const text = 'approved';
    return (
      <ConfirmationDialog
        open={true}
        onAccept={async () => {
          await updateOrderStatus(editingStatusOrder.id, EOrderStatus.Approved);
          setEditingStatusOrder(null);
        }}
        onDecline={async () => {
          setEditingStatusOrder(null);
        }}
        prompt={`Are you sure you would like to update ${editingStatusOrder.consumerInformation.name}'s
         order to the status "${text}?"`}
        positiveText={`Mark as ${text}`}
        negativeText={'Cancel'}
        positiveVariant={'success'}
        negativeVariant={'danger'}
      />
    );
  };

  const renderRedirect = () => {
    if (redirectUrl.length !== 0) {
      return <Navigate to={redirectUrl} />;
    }
    return null;
  };


  const getDisabledToolTipText = (order: Order) => {
    if (order.orderStatus === EOrderStatus.Cancelled)
      return 'Order is cancelled';
    if (order.orderStatus === EOrderStatus.Unapproved)
      return 'Order needs approval first';
    if (order.orderStatus === EOrderStatus.Approved)
      return 'Order has already been approved';
    return '';
  };

  const actionsFormatter = useCallback((cell: any, order: Order) => {
    const approveEnabled = order.orderStatus === EOrderStatus.Unapproved;
    const shipEnabled = order.orderStatus === EOrderStatus.Approved;
    return (
      <div className={gridStyles['table-actions-wrapper']}>
        {order.orderStatus !== EOrderStatus.Cancelled ? (
          <>
            <div>
              <AdministratorComponent>
                <IconButton
                  icon={'history'}
                  styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'History'}
                  onClick={() => { setAuditEntityId(order.id); setAuditOpen(true); }}
                />
              </AdministratorComponent>
            </div>
            <div>
              <IconButton
                icon={'eye'}
                styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                size={'1x'}
                iconToolTipText={'View'}
                onClick={() => setViewingOrderId(order.id)}
              />
            </div>
            <AdministratorComponent>
              <div>
                <IconButton
                  icon={'pen'}
                  styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'Edit'}
                  onClick={() => setEditingOrderData({editing: true, id: order.id})}
                />
              </div>
            </AdministratorComponent>
            <RoleProtectedComponent allowedRoles={['Volunteer', 'Warehouse', 'Administrator']}>
              <div>
                <IconButton
                  icon={'x'}
                  styles={{color: AppTheme.colors.danger, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'Cancel'}
                  onClick={() => setCancellingOrder(order)}
                />
              </div>
            </RoleProtectedComponent>
            <RoleProtectedComponent allowedRoles={['Volunteer', 'Warehouse', 'Administrator']}>
              <div>
                <IconButton
                  icon={'check'}
                  styles={{color: approveEnabled ? AppTheme.colors.themeGreen : AppTheme.colors.gray400, marginLeft: '.5rem'}}
                  size={'1x'}
                  disabled={!approveEnabled}
                  iconToolTipText={approveEnabled ? 'Mark as approved' : getDisabledToolTipText(order)}
                  onClick={() => setEditingStatusOrder(order)}
                />
              </div>
            </RoleProtectedComponent>
        </> ): (
          <>
            <div>
              <AdministratorComponent>
                <IconButton
                  icon={'history'}
                  styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'History'}
                  onClick={() => { setAuditEntityId(order.id); setAuditOpen(true); }}
                />
              </AdministratorComponent>
            </div>
            <div>
              <IconButton
                icon={'eye'}
                styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                size={'1x'}
                iconToolTipText={'View'}
                onClick={() => setViewingOrderId(order.id)}
              />
            </div>
            <RoleProtectedComponent allowedRoles={['Volunteer', 'Warehouse', 'Administrator']}>
              <div>
                <IconButton
                  icon={'pen'}
                  styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'Edit'}
                  onClick={() => setEditingOrderData({editing: true, id: order.id})}
                />
              </div>
            </RoleProtectedComponent>
            <RoleProtectedComponent allowedRoles={['Volunteer', 'Warehouse', 'Administrator']}>
              <div>
                <IconButton
                  icon={'trash-alt'}
                  styles={{color: AppTheme.colors.danger, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'Permenantly delete order'}
                  onClick={() => setDeletingOrder(order)}
                />
              </div>
            </RoleProtectedComponent>
          </>
    )}
      </div>
    );
  }, []);

  const dateTimeFormatter = (isoDateTime: string) => {
    if (isNullOrWhitespace(isoDateTime)) return '';
    return format(utcToZonedTime(parseISO(isoDateTime), localTz as any as string), 'MM/dd/yyyy hh:mm:ss a', {timeZone: localTz as any as string});
  };


  const columns: MRT_ColumnDef<Order>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row) => row.consumerInformation.name,
      enableSorting: true,
    },
    {
      id: 'programOrDepartment',
      header: 'Program/Department',
      accessorFn: (row) => row.programId,
      Cell: ({ row }: any) => <ConnectedProgramName programId={row.original.programId} fallback={row.original.consumerInformation.programOrDepartment} />,
      enableSorting: true,
    },
    {
      id: 'houseOrNeighborhood',
      header: 'House/Neighborhood',
      accessorFn: (row) => row.houseId,
      Cell: ({ row }: any) => <ConnectedHouseName houseId={row.original.houseId} fallback={row.original.consumerInformation.houseOrNeighborhood} />,
      enableSorting: true,
    },
    {
      id: 'phoneNumber',
      header: 'Phone',
      accessorFn: (row) => formatIfPossibleUsPhone(row.consumerInformation.phoneNumber),
      enableSorting: true,
    },
    {
      id: 'orderStatus',
      header: 'Status',
      accessorFn: (row) => EOrderStatus[row.orderStatus],
      enableSorting: true,
    },
    {
      id: 'submittedAt',
      header: `Submitted At (${localTzFriendlyFormat})`,
      // Use a sortable primitive (epoch ms). Display formatted string in Cell.
      accessorFn: (row) => (row.submittedAt ? parseISO(row.submittedAt).getTime() : 0),
      Cell: ({ row }) => dateTimeFormatter((row.original as Order).submittedAt),
      enableSorting: true,
    },
    {
      id: 'approvedAt',
      header: `Validated at (${localTzFriendlyFormat})`,
      // Use a sortable primitive (epoch ms). Display formatted string in Cell.
      accessorFn: (row) => (row.approvedAt ? parseISO(row.approvedAt).getTime() : 0),
      Cell: ({ row }) => dateTimeFormatter((row.original as Order).approvedAt),
      enableSorting: true,
    },
    {
      id: 'storeItems',
      header: 'Store Items',
      accessorFn: (row) => orderStore.selectors.getTotalStoreItems({} as any)(row),
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'warehouseItems',
      header: 'Warehouse Items',
      accessorFn: (row) => orderStore.selectors.getTotalWarehouseItems({} as any)(row),
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'totalItems',
      header: 'Total Items',
      accessorFn: (row) => orderStore.selectors.getTotalStoreItems({} as any)(row) + orderStore.selectors.getTotalWarehouseItems({} as any)(row),
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'uniqueItems',
      header: 'Unique Items',
      accessorFn: (row) => row.orderItems.length,
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'totalCost',
      header: 'Total Cost',
      accessorKey: 'totalCost',
      Cell: ({ cell }) => formatCentsToUSD(cell.getValue<number>() ?? 0),
      enableSorting: true
    },
    {
      id: 'actions',
      header: 'Actions',
      accessorFn: () => '',
      Cell: ({ row }) => actionsFormatter(null, row.original as Order),
      size: 0,
      minSize: 0,
      maxSize: 0,
    },
  ], []);

  return (
    <>
      {renderRedirect()}
      <Paper withBorder shadow="sm" radius="md" p="sm" className={gridStyles['responsive-table-wrapper']}>
        <StyledMantineTable
          columns={columns}
          data={filteredOrders}
          printRef={printRef}
          initialState={{
            columnVisibility: {
              phoneNumber: false,
              address: false,
              approvedAt: false
            },
            pagination: {pageSize: 15}
          }}
          includeAllRowsOption={true}
          topToolbarActions={
            <div className={gridStyles['button-group']}>
              <Button onClick={() => setCreatingOrder(makeOrder())} style={{ backgroundColor: AppTheme.colors.themePurple }}>
                Withdraw
              </Button>
              <RoleProtectedComponent allowedRoles={['Administrator']}>
                <Button onClick={() => setProgramModalOpened(true)} style={{ backgroundColor: AppTheme.colors.themePurple }}>
                  Programs
                </Button>
              </RoleProtectedComponent>
              <RoleProtectedComponent allowedRoles={['Administrator']}>
                <Button onClick={() => setHouseModalOpened(true)} style={{ backgroundColor: AppTheme.colors.themePurple }}>
                  Houses
                </Button>
              </RoleProtectedComponent>
              <Button onClick={handleprint} style={{ backgroundColor: AppTheme.colors.themePurple }}>
                Print Table
              </Button>
              <Select
                menuPlacement={'auto'}
                value={orderFilter}
                options={orderFilterDropdownOptions}
                isClearable={false}
                isOptionSelected={(val) => val === orderFilter}
                isSearchable={false}
                onChange={(newValue) => setOrderFilter(newValue ?? defaultFilterValue)}
                menuPortalTarget={document.querySelector('body')}
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 3000, overflowY: 'visible', transform: 'translate(0, -8px)' }),
                  control: (base: any) => ({ ...base, minWidth: '12rem', marginLeft: '.5rem' }),
                }}
              />
            </div>
          }
          mantineTableProps={{
            striped: true,
            highlightOnHover: true,
            withColumnBorders: true,
            className: gridStyles['table-auto'],
          }}
        />
      </Paper>

      {getOrderById(editingOrderData.id) ? (
        <OrderModal
          show={editingOrderData.editing}
          order={getOrderById(editingOrderData.id)}
          // If the modal is opened via URL param, default to view (read-only) mode
          readOnlyInitial={!!paramsId}
          onSubmit={() => {
            setEditingOrderData({ editing: false, id: '' });
            if (paramsId) {
              navigate(RoutePaths.privatePaths.configurationPaths.withdrawalManagement);
            }
          }}
          onCancel={() => {
            setEditingOrderData({ editing: false, id: '' });
            if (paramsId) {
              navigate(RoutePaths.privatePaths.configurationPaths.withdrawalManagement);
            }
          }}
        />
      ) : null}

      {creatingOrder ? (
        <OrderModal
          show={true}
          order={creatingOrder}
          onSubmit={() => setCreatingOrder(null)}
          onCancel={() => setCreatingOrder(null)}
        />
      ) : null}

      {getOrderById(viewingOrderId) ? (
        <OrderModal
          show={!!viewingOrderId}
          order={getOrderById(viewingOrderId)}
          readOnlyInitial={true}
          onSubmit={() => setViewingOrderId('')}
          onCancel={() => setViewingOrderId('')}
        />
      ) : null}

      {isDeleting !== '' && (
        <ConfirmationDialog
          onAccept={async () => {
            setIsDeleting('');
          }}
          onDecline={async () => {
            setIsDeleting('');
          }}
          open={isDeleting !== ''}
          prompt='Are you sure you want to remove this item?'
          positiveText='Yes'
          negativeText='No'
          positiveVariant='success'
          negativeVariant='danger'
        />
      )}

      {renderStatusSubmitModal()}
      {renderCancellationModal()}
      {renderDeletionModal()}

      <AuditLogLoader
        opened={auditOpen}
        onClose={() => setAuditOpen(false)}
        entityId={auditEntityId}
      />

      <RoleProtectedComponent allowedRoles={['Administrator']}>
        <ProgramModal
          opened={programModalOpened}
          onClose={() => setProgramModalOpened(false)}
        />
      </RoleProtectedComponent>

      <RoleProtectedComponent allowedRoles={['Administrator']}>
        <HouseModal
          opened={houseModalOpened}
          onClose={() => setHouseModalOpened(false)}
        />
      </RoleProtectedComponent>
    </>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({
    updateOrderStatus: orderStore.actions.updateOrderStatus,
    cancelOrder: orderStore.actions.cancelOrder,
    deleteOrder: orderStore.actions.deleteOrder
  }, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  orders: orderStore.selectors.withdrawals(state),
  unapprovedOrders: orderStore.selectors.unapproved(state),
  approvedOrders: orderStore.selectors.approved(state),
  cancelledOrders: orderStore.selectors.cancelled(state),
  notCancelledOrders: orderStore.selectors.notCancelled(state),
  getOrderById: orderStore.selectors.getById(state),
  programs: programStore.selectors.getAsArray(state),
  houses: houseStore.selectors.getAsArray(state)
});
export default connect(mapStateToProps, mapDispatchToProps)(OrderGrid);
